/**
 * Manual-quote requirements (R18 two-surfaces doctrine): the SAME toggle
 * component as add-ons, expanded inline, minus the price-only extras —
 * registry-driven filter: bool add-ons that are not airport-priced upsells
 * (drops the count stepper + Meet & greet; keeps returnTrip, lateNight,
 * trailer, accessibleVehicle — trailer/lateNight prominent, the 362 rider).
 * The red 'Edited' badge compares against the IMMUTABLE customer baseline;
 * switching a requested accessible vehicle OFF warns, never blocks (Q24).
 */
import { Fragment, type ComponentChildren } from 'preact';
import type { CustomerFlags, QuoteOptions } from '../store/state.js';
import { addOnsForService, type Registry } from '../store/registry.js';
import { Banner } from './Banner.js';

export type RequirementsProps = {
  registry: Registry;
  service: string;
  options: QuoteOptions;
  baseline: CustomerFlags;
  onChange: (key: keyof QuoteOptions, value: boolean) => void;
  /** Priced (grid/p2p) journeys also show the upsell that PRICES for this
   *  service — restoring the customer's airport meet & greet. Default off, so
   *  the manual path renders exactly what it always did. */
  includePricedUpsell?: boolean;
  /**
   * ⭐⭐ T1717 M6 (DD6, P0-11) — WHAT THE **NURSING HOME** ANSWERED about the
   * return, or null when this job did not come from the portal.
   *
   * ⛔ NULL RENDERS NOTHING AT ALL, and that is the whole reason it is not a
   * boolean. A web enquiry and a walk-in have no home behind them; a screen that
   * said "The home said: one way" over a customer's own tick would attribute her
   * answer to a third party who never saw the booking. Three states, three
   * values.
   *
   * ⛔ IT IS NOT `baseline.returnTrip` EITHER, EVEN THOUGH THE STORE NOW SEEDS
   * THAT FROM IT. `baseline` is the whole immutable flag bag and drives the
   * `Edited` chip for six add-ons; this drives a NAMED sentence and a warn
   * banner about ONE of them, and only where the answer is a home's to give.
   * DD6 rules the override field-scoped to `returnTrip`; a banner keyed on the
   * bag would eventually speak for `accessibleVehicle`, which is Article 9.
   *
   * ⛔ IT WARNS, IT NEVER BLOCKS (the Q24 posture the accessible-vehicle banner
   * below already takes). Helen is on the phone to the home; she may know
   * something the form does not.
   */
  homeReturnTrip?: boolean | null;
  /** ⭐ board 2871 - what renders directly under the Return trip row (the Quote
   *  screen's Return date and Return time). The host decides when; absent renders
   *  nothing, so every other host is unchanged. */
  returnLeg?: ComponentChildren;
};

export function requirementAddOns(registry: Registry, service: string) {
  return addOnsForService(registry, service).filter(
    (a) => a.valueKind === 'bool' && a.pricesFor !== 'airport',
  );
}

/**
 * The priced path's set: the structured flags PLUS any bool add-on whose pricing
 * actually applies to THIS service. The shipped filter drops meet & greet
 * wholesale, so an airport lead that ASKED for it (the public form captures it)
 * arrived at the quote with no way to correct it. Keyed on the service, not the
 * screen, so a golf or local job is still never offered a toggle that cannot
 * price there.
 */
export function pricedRequirementAddOns(registry: Registry, service: string) {
  return addOnsForService(registry, service).filter(
    (a) => a.valueKind === 'bool' && (a.pricesFor !== 'airport' || service === 'airport'),
  );
}

export function Requirements({
  registry,
  service,
  options,
  baseline,
  onChange,
  includePricedUpsell,
  homeReturnTrip,
  returnLeg,
}: RequirementsProps) {
  const reqs = includePricedUpsell
    ? pricedRequirementAddOns(registry, service)
    : requirementAddOns(registry, service);
  // A service the registry does not know (a Phase-2 booking type re-opened in
  // the calculator) has no add-ons — render nothing rather than a heading over
  // an empty list.
  if (reqs.length === 0) return null;
  const accessibleSwitchedOff =
    baseline?.accessibleVehicle === true && options.accessibleVehicle === false;
  /**
   * ⭐⭐ T1717 M6 (DD6). The home answered, and the operator's switch disagrees.
   *
   * ⛔ BOTH DIRECTIONS, ONE CONDITION. The plan and the Q24 posture are explicit
   * that turning a return OFF and turning one ON are both overrides worth
   * showing: off under-prices and under-dispatches a run the home is expecting
   * back, on doubles a bill the home did not ask for. An asymmetric check would
   * have caught only whichever direction its author happened to picture.
   * ⛔ `homeReturnTrip == null` (no portal answer) falls out here rather than
   * being tested separately: `null !== true` and `null !== false` would both be
   * true, so a bare inequality would warn on every ordinary booking.
   */
  const returnAnswerOverridden =
    homeReturnTrip != null && homeReturnTrip !== Boolean(options.returnTrip);

  return (
    <div class="requirements">
      <h2 class="field-heading">Requirements</h2>
      <div class="toggle-list">
        {reqs.map((a) => {
          const key = a.engineKey as keyof QuoteOptions;
          const on = Boolean(options[key]);
          const edited = baseline !== null && baseline[key] !== undefined && baseline[key] !== on;
          /**
           * ⭐⭐ T1717 M6 (DD6) — THE HOME'S ANSWER SITS UNDER ITS OWN SWITCH,
           * OUTSIDE THE BUTTON.
           *
           * ⛔ NOT INSIDE IT, AND THAT IS AN ACCESSIBILITY DECISION RATHER THAN
           * A LAYOUT ONE. A `<button role="switch">` is named from its contents,
           * so a sentence in there would make the control announce "Return trip
           * The home said comes back, switch, on" — a sentence read as a
           * control's name. The `Edited` chip is one word and already lives
           * inside; a statement about a third party does not belong there.
           * ⛔ AND IT IS KEYED ON `engineKey`, WHICH IS THE ONE PLACE THAT IS
           * CORRECT — this is a per-add-on FACT rendered next to its own row,
           * not a policy branch inside a registry loop (contrast
           * notify/driver-message.ts, where gating the loop on an engineKey is
           * forbidden because the registry is the allowlist there).
           */
          const homeLine =
            a.engineKey === 'returnTrip' && homeReturnTrip != null
              ? homeReturnTrip
                ? 'The home said: comes back'
                : 'The home said: one way'
              : null;
          return (
            <Fragment key={a.engineKey}>
              <button
                type="button"
                class={`toggle-row toggle-row--tappable${on ? ' toggle-row--on' : ''}`}
                role="switch"
                aria-checked={on}
                onClick={() => onChange(key, !on)}
              >
                <span class="toggle-label">{a.label}</span>
                {edited && <span class="chip chip--edited">Edited</span>}
                <span class="toggle-knob" aria-hidden="true" />
              </button>
              {homeLine !== null && <p class="field-hint">{homeLine}</p>}
              {a.engineKey === 'returnTrip' && returnLeg}
            </Fragment>
          );
        })}
      </div>
      {accessibleSwitchedOff && (
        <Banner kind="warn">
          The customer asked for an accessible vehicle. With this off, the vehicle may not suit them.
        </Banner>
      )}
      {/* ⭐⭐ T1717 M6 (DD6, P0-11) — THE OVERRIDE, SAID OUT LOUD, IN BOTH
          DIRECTIONS. ⛔ WARN, NEVER BLOCK: the Q24 posture the banner above
          already takes. Helen is on the phone to the ward and may know something
          the form does not; the point is that she cannot change the home's
          answer by accident and not notice.
          ⚠️ EACH SENTENCE NAMES THE CONSEQUENCE, not just the disagreement. "It
          is priced and driven as one way" is the fact she can act on; "these
          differ" is not. */}
      {returnAnswerOverridden && (
        <Banner kind="warn">
          {homeReturnTrip === true
            ? 'The home said this journey comes back. With this off it is priced and driven as one way.'
            : 'The home said this journey is one way. With this on it is priced and driven as a return.'}
        </Banner>
      )}
    </div>
  );
}
